/**
 * Deterministic trust signals from listing fields (web + mobile).
 * AI photo fields are optional evidence — they never invent the score.
 */

import { hasBatteryTransparency } from './batteryTransparency'
import {
  hasDeclaredParts,
  normalizePartsAuthenticity,
  showFaceIdPart,
  visiblePartIds,
  type PartsAuthenticityMap,
} from './partsAuthenticity'
import { TRUST_INSPECTION_47_TOTAL } from './trustInspection47'

export type CosmeticGrade = 'like_new' | 'excellent' | 'good' | 'budget'

export type PriceInsightLevel = 'great' | 'fair' | 'above' | 'unknown'

export type TrustCheckStatus = 'pass' | 'warn' | 'unknown' | 'na'

export interface ListingTrustInput {
  id: number
  price: number
  condition: string
  in_box_completeness?: string | null
  battery_health?: number | null
  imei?: string | null
  imei_verified?: boolean
  has_factory_warranty?: boolean
  warranty_remaining?: string | null
  usage_duration?: string | null
  shipment_method?: string | null
  catalog_rank_group?: number
  ai_recommended_eur?: number | null
  phone_model?: { manufacturer?: string; model_name?: string; release_year?: number | null } | null
  created_at?: string
  parts_authenticity?: PartsAuthenticityMap | Record<string, { status?: string; grade?: string | null } | null> | null
  photo_quality?: string | null
  photo_suggested_condition?: string | null
  model_match?: string | null
}

export interface TrustCheckItem {
  id: string
  labelKey: string
  valueKey: string
  valueVars?: Record<string, string | number>
  status: TrustCheckStatus
}

export interface DevicePassport {
  passportId: string
  deviceTitle: string
  inspectionDate: string | null
  batteryPct: number | null
  diagnosticsPassed: number
  diagnosticsTotal: number
  diagnosticsInspected: boolean
  warrantyLabel: string
}

const VERIFIED_DIAGNOSTICS_TOTAL = TRUST_INSPECTION_47_TOTAL

const CONDITION_RANK: Record<string, number> = {
  new_sealed: 6,
  unused: 5,
  excellent: 4,
  good: 3,
  fair: 2,
  damaged: 1,
}

function slugPart(s: string): string {
  return (s || 'device')
    .replace(/[^a-zA-Z0-9]+/g, '')
    .slice(0, 6)
    .toUpperCase() || 'PHONE'
}

function declaredBatteryPct(listing: ListingTrustInput): number | null {
  const bat = listing.battery_health
  if (bat == null || !Number.isFinite(bat) || bat <= 0) return null
  return bat
}

const PART_ORIGINAL_BONUS = 5
const PART_OEM_BONUS = 2
const PART_AFTERMARKET_PENALTY = 10

const USAGE_DURATION_KEYS = ['1_3m', '3_6m', '6_12m', '1_2y', '2y_plus', 'unknown'] as const

function normalizeUsageDuration(raw?: string | null): (typeof USAGE_DURATION_KEYS)[number] | null {
  if (!raw) return null
  const v = raw.trim()
  if ((USAGE_DURATION_KEYS as readonly string[]).includes(v)) {
    return v as (typeof USAGE_DURATION_KEYS)[number]
  }
  const low = v.toLowerCase()
  if (/1\s*[–-]\s*3|1_3/.test(low)) return '1_3m'
  if (/3\s*[–-]\s*6|3_6/.test(low)) return '3_6m'
  if (/6\s*[–-]\s*12|6_12|6–12/.test(low)) return '6_12m'
  if (/1\s*[–-]\s*2|1_2/.test(low)) return '1_2y'
  if (/2\s*\+|2y_plus|2\+/.test(low)) return '2y_plus'
  return null
}

/** Bonus taškai pagal deklaruotą naudojimo trukmę. */
export function usageDurationBonus(raw?: string | null): number {
  switch (normalizeUsageDuration(raw)) {
    case '1_3m':
      return 5
    case '3_6m':
      return 4
    case '6_12m':
      return 3
    case '1_2y':
      return 1
    default:
      return 0
  }
}

export function isLightUsage(raw?: string | null): boolean {
  return usageDurationBonus(raw) >= 3
}

export function countOriginalParts(listing: ListingTrustInput): number {
  const parts = normalizePartsAuthenticity(listing.parts_authenticity)
  const ids = visiblePartIds(
    listing.phone_model?.manufacturer,
    listing.phone_model?.model_name,
    parts,
  )
  return ids.filter((id) => parts[id]?.status === 'original').length
}

/** Peer skelbimų „plusai“ patikros ataskaitos viršuje. */
export function declarationHighlights(
  listing: ListingTrustInput,
): Array<{ key: string; vars?: Record<string, string | number> }> {
  if (isPlatformVerifiedListing(listing)) return []

  const out: Array<{ key: string; vars?: Record<string, string | number> }> = []
  const originalCount = countOriginalParts(listing)
  if (originalCount >= 3) {
    out.push({ key: 'trust.report.highlightOriginalParts', vars: { count: originalCount } })
  }
  if (listing.has_factory_warranty) {
    out.push({ key: 'trust.report.highlightFactoryWarranty' })
  }
  if (isLightUsage(listing.usage_duration)) {
    out.push({ key: 'trust.report.highlightLightUsage' })
  }
  if ((listing.in_box_completeness || '').toLowerCase() === 'full') {
    out.push({ key: 'trust.report.highlightFullKit' })
  }
  return out
}

/** Pardavėjo deklaracija 0–100 — originalios dalys, baterija, garantija. */
export function sellerDeclarationScore100(listing: ListingTrustInput): number {
  const parts = normalizePartsAuthenticity(listing.parts_authenticity)
  const ids = visiblePartIds(
    listing.phone_model?.manufacturer,
    listing.phone_model?.model_name,
    parts,
  )

  let score = hasDeclaredParts(parts) ? 52 : 48
  let declared = 0
  let original = 0
  let aftermarket = 0

  for (const id of ids) {
    const st = parts[id]?.status
    if (!st || st === 'unknown') continue
    declared += 1
    if (st === 'original') {
      original += 1
      score += PART_ORIGINAL_BONUS
    } else if (st === 'replaced_oem') {
      score += PART_OEM_BONUS
    } else if (st === 'replaced_aftermarket' || st === 'replaced') {
      aftermarket += 1
      score -= PART_AFTERMARKET_PENALTY
    }
  }

  if (declared > 0 && original === declared && aftermarket === 0) {
    score += 8
  }

  const bat = declaredBatteryPct(listing)
  if (bat != null) {
    if (bat >= 98) score += 8
    else if (bat >= 90) score += 5
    else if (bat >= 80) score += 2
    else if (bat < 70) score -= 4
  }

  if (listing.has_factory_warranty) score += 6
  if (listing.imei_verified) score += 4
  else if (listing.imei) score += 2

  score += usageDurationBonus(listing.usage_duration)

  if ((listing.in_box_completeness || '').toLowerCase() === 'full') {
    score += 3
  }

  return Math.max(0, Math.min(100, score))
}

function photoTrustDelta(listing: ListingTrustInput): number {
  let delta = 0
  const quality = (listing.photo_quality || '').toLowerCase()
  if (quality === 'unusable') delta -= 6
  else if (quality === 'low') delta -= 3

  const match = (listing.model_match || '').toLowerCase()
  if (match === 'mismatch') delta -= 8
  else if (match === 'match') delta += 2

  const suggested = (listing.photo_suggested_condition || '').toLowerCase()
  const declaredRank = CONDITION_RANK[listing.condition] ?? 0
  const suggestedRank = CONDITION_RANK[suggested] ?? 0
  if (suggestedRank > 0 && declaredRank > 0 && suggestedRank < declaredRank) {
    delta -= declaredRank - suggestedRank >= 2 ? 6 : 4
  }
  return delta
}

/** Cosmetic grade label bucket for buyer-facing copy. */
export function cosmeticGradeFromCondition(condition: string): {
  grade: CosmeticGrade
  labelKey: string
  scoreMin: number
  scoreMax: number
} {
  switch (condition) {
    case 'new_sealed':
    case 'unused':
      return { grade: 'like_new', labelKey: 'trust.grade.likeNew', scoreMin: 9.5, scoreMax: 10 }
    case 'excellent':
      return { grade: 'excellent', labelKey: 'trust.grade.excellent', scoreMin: 8.5, scoreMax: 9.4 }
    case 'good':
      return { grade: 'good', labelKey: 'trust.grade.good', scoreMin: 7, scoreMax: 8.4 }
    default:
      return { grade: 'budget', labelKey: 'trust.grade.budget', scoreMin: 0, scoreMax: 6.9 }
  }
}

/** Technical condition 0–10 from declared condition + battery. */
export function technicalConditionScore(listing: ListingTrustInput): number {
  const base: Record<string, number> = {
    new_sealed: 10,
    unused: 9.8,
    excellent: 9,
    good: 8,
    fair: 6.5,
    damaged: 4,
  }
  let score = base[listing.condition] ?? 7
  const bat = declaredBatteryPct(listing)
  if (bat != null) {
    const batNorm = Math.min(10, bat / 10)
    score = score * 0.75 + batNorm * 0.25
  }
  return Math.round(score * 10) / 10
}

/** Cosmetic score — slightly below technical unless excellent+ with strong battery. */
export function cosmeticConditionScore(listing: ListingTrustInput): number {
  const tech = technicalConditionScore(listing)
  const { scoreMax } = cosmeticGradeFromCondition(listing.condition)
  const bat = declaredBatteryPct(listing)
  if (
    bat != null &&
    bat >= 90 &&
    (listing.condition === 'excellent' ||
      listing.condition === 'unused' ||
      listing.condition === 'new_sealed')
  ) {
    return Math.min(scoreMax, Math.round(tech * 10) / 10)
  }
  return Math.min(scoreMax, Math.round(tech * 0.95 * 10) / 10)
}

/** Komplektacija 0–100 (full > partial > phone_only). */
function kitScore100(inBox?: string | null): number {
  switch ((inBox || '').toLowerCase()) {
    case 'full':
      return 96
    case 'partial':
      return 90
    case 'phone_only':
      return 82
    default:
      return 86
  }
}

/** Kosmetinė būklė 0–100. */
function cosmeticScore100(listing: ListingTrustInput): number {
  let score = cosmeticConditionScore(listing) * 10
  const bat = declaredBatteryPct(listing)
  if (bat == null && listing.condition !== 'new_sealed' && listing.condition !== 'unused') {
    score *= 0.96
  }
  return score
}

/** reDevicer kaina vs rinkos kaina 0–100. */
function priceValueScore100(listing: ListingTrustInput): number {
  const market = listing.ai_recommended_eur
  if (market == null || !Number.isFinite(market) || market <= 0 || listing.price <= 0) {
    return 74
  }
  const savingsRatio = market / listing.price
  if (savingsRatio >= 1.15) return 96
  if (savingsRatio >= 1.05) return 88
  if (savingsRatio >= 0.95) return 78
  if (savingsRatio >= 0.88) return 66
  return 56
}

/** Rinkos etalono pasitikėjimas 0–100. */
function marketReferenceScore100(listing: ListingTrustInput): number {
  const market = listing.ai_recommended_eur
  if (market != null && Number.isFinite(market) && market > 0) return 88
  return 72
}

/** Sandėlio / patikrinto inventoriaus patikimumo koeficientas. */
function platformTrustFactor(listing: ListingTrustInput): number {
  let factor = 1.0
  if (isPlatformVerifiedListing(listing)) factor *= 1.05
  if (listing.imei_verified) factor *= 1.03
  return factor
}

/** Papildoma nuotraukų įrodymų korekcija (± keli taškai). */
function evidenceTrustFactor(listing: ListingTrustInput): number {
  const delta = photoTrustDelta(listing)
  return 1 + delta / 120
}

type TrustWeights = {
  kit: number
  cosmetic: number
  declaration: number
  price: number
  market: number
}

/** Peer skelbimai — įrenginio kokybė; kaina rodoma atskirai (PriceInsightBadge). */
const TRUST_WEIGHTS_PEER: TrustWeights = {
  kit: 0.15,
  cosmetic: 0.38,
  declaration: 0.42,
  price: 0,
  market: 0.05,
}

/** Sandėlio patikra — kainos vertė vis dar įtraukta. */
const TRUST_WEIGHTS_VERIFIED: TrustWeights = {
  kit: 0.14,
  cosmetic: 0.32,
  declaration: 0.24,
  price: 0.18,
  market: 0.12,
}

function trustWeights(listing: ListingTrustInput): TrustWeights {
  return isPlatformVerifiedListing(listing) ? TRUST_WEIGHTS_VERIFIED : TRUST_WEIGHTS_PEER
}

/**
 * Patikros ataskaitos taškai 0–100.
 * Peer: komplektacija + kosmetika + pardavėjo deklaracija (dalys, baterija, garantija).
 * Kainos palyginimas — atskirame PriceInsightBadge, ne čia.
 */
export function computeTrustScore(listing: ListingTrustInput): number {
  const weights = trustWeights(listing)
  const weighted =
    kitScore100(listing.in_box_completeness) * weights.kit +
    cosmeticScore100(listing) * weights.cosmetic +
    sellerDeclarationScore100(listing) * weights.declaration +
    priceValueScore100(listing) * weights.price +
    marketReferenceScore100(listing) * weights.market

  const raw = weighted * platformTrustFactor(listing) * evidenceTrustFactor(listing)

  return Math.max(0, Math.min(100, Math.round(raw)))
}

export function isPlatformVerifiedListing(listing: ListingTrustInput): boolean {
  return listing.shipment_method === 'redevicer_warehouse'
}

export function priceInsight(
  listing: Pick<ListingTrustInput, 'price' | 'ai_recommended_eur'>,
): {
  level: PriceInsightLevel
  marketEur: number | null
  savingsEur: number | null
  savingsPct: number | null
} {
  const rec = listing.ai_recommended_eur
  if (rec == null || !Number.isFinite(rec) || rec <= 0) {
    return { level: 'unknown', marketEur: null, savingsEur: null, savingsPct: null }
  }
  const market = Math.round(rec)
  const diff = market - listing.price
  const pct = Math.round((diff / market) * 100)
  if (pct >= 5) {
    return { level: 'great', marketEur: market, savingsEur: diff, savingsPct: pct }
  }
  if (pct <= -5) {
    return { level: 'above', marketEur: market, savingsEur: diff, savingsPct: pct }
  }
  return { level: 'fair', marketEur: market, savingsEur: diff, savingsPct: pct }
}

/** Rinkos kainos rodymui — suapvalinta iki artimiausio €5. */
export function approxMarketEur(eur: number): number {
  return Math.round(eur / 5) * 5
}

export function formatApproxMarketEur(eur: number): string {
  return `≈ €${approxMarketEur(eur)}`
}

/** i18n key (+ optional vars) for compact or detailed price insight copy. */
export function priceInsightLabel(
  level: PriceInsightLevel,
  insight: { savingsPct?: number | null },
  compact = false,
): { key: string; vars?: Record<string, string | number> } | null {
  if (level === 'unknown') return null
  if (compact) {
    return { key: `listing.priceInsight.${level}` }
  }
  if (level === 'great' && insight.savingsPct != null) {
    return { key: 'trust.report.greatPrice', vars: { pct: insight.savingsPct } }
  }
  if (level === 'above' && insight.savingsPct != null) {
    return { key: 'listing.priceInsight.aboveDetail', vars: { pct: Math.abs(insight.savingsPct) } }
  }
  if (level === 'fair') {
    return { key: 'listing.priceInsight.fair' }
  }
  return { key: `listing.priceInsight.${level}` }
}

export function devicePassportId(listing: ListingTrustInput): string {
  const model = listing.phone_model
  const slug = slugPart(model?.model_name || model?.manufacturer || 'RD')
  const id = String(listing.id).padStart(6, '0')
  return `RD-${slug}-${id}`
}

export function buildDevicePassport(listing: ListingTrustInput): DevicePassport {
  const model = listing.phone_model
  const title = model
    ? `${model.manufacturer ?? ''} ${model.model_name ?? ''}`.trim()
    : 'Device'
  const inspected = isPlatformVerifiedListing(listing)

  let warrantyLabel = 'Seller declared'
  if (listing.has_factory_warranty) {
    warrantyLabel = listing.warranty_remaining ? 'Factory warranty' : 'Warranty — yes'
  } else if (inspected) {
    warrantyLabel = '12 months reDevicer'
  }

  return {
    passportId: devicePassportId(listing),
    deviceTitle: title,
    inspectionDate: inspected ? listing.created_at?.slice(0, 10) ?? null : null,
    batteryPct: declaredBatteryPct(listing),
    diagnosticsPassed: inspected ? VERIFIED_DIAGNOSTICS_TOTAL : 0,
    diagnosticsTotal: inspected ? VERIFIED_DIAGNOSTICS_TOTAL : 0,
    diagnosticsInspected: inspected,
    warrantyLabel,
  }
}

/** Buyer-facing checklist — platform verified shows full grid; peer shows declared facts. */
export function trustChecklist(listing: ListingTrustInput): TrustCheckItem[] {
  const verified = isPlatformVerifiedListing(listing)
  const bat = declaredBatteryPct(listing)
  const parts = normalizePartsAuthenticity(listing.parts_authenticity)
  const declaredParts = hasDeclaredParts(parts)
  const aftermarket = Object.values(parts).some(
    (p) => p?.status === 'replaced_aftermarket' || p?.status === 'replaced',
  )
  const originalCount = Object.values(parts).filter((p) => p?.status === 'original').length

  const cosmeticScore = cosmeticConditionScore(listing)
  const usageKey = normalizeUsageDuration(listing.usage_duration)

  const items: TrustCheckItem[] = [
    {
      id: 'battery',
      labelKey: 'trust.check.battery.label',
      valueKey: bat != null ? 'trust.check.battery.valuePct' : 'trust.check.notProvided',
      valueVars: bat != null ? { pct: bat } : undefined,
      status: bat != null && bat >= 80 ? 'pass' : 'warn',
    },
    {
      id: 'display',
      labelKey: 'trust.check.display.label',
      valueKey:
        verified || parts.display?.status === 'original'
          ? 'trust.check.display.verified'
          : 'trust.check.display.declared',
      status: verified || parts.display?.status === 'original' ? 'pass' : 'unknown',
    },
    {
      id: 'face_id',
      labelKey: 'trust.check.faceId.label',
      valueKey:
        verified || parts.face_id?.status === 'original'
          ? 'trust.check.faceId.verified'
          : 'trust.check.notInspected',
      status: verified || parts.face_id?.status === 'original' ? 'pass' : 'na',
    },
    {
      id: 'cameras',
      labelKey: 'trust.check.cameras.label',
      valueKey:
        verified || parts.camera?.status === 'original'
          ? 'trust.check.cameras.verified'
          : 'trust.check.notInspected',
      status: verified || parts.camera?.status === 'original' ? 'pass' : 'na',
    },
    {
      id: 'imei',
      labelKey: 'trust.check.imei.label',
      valueKey: listing.imei_verified
        ? 'trust.check.imei.verified'
        : listing.imei
          ? 'trust.check.imei.provided'
          : 'trust.check.notProvided',
      status: listing.imei_verified ? 'pass' : listing.imei ? 'warn' : 'unknown',
    },
    {
      id: 'activation',
      labelKey: 'trust.check.activationLock.label',
      valueKey: listing.imei_verified
        ? 'trust.check.activationLock.off'
        : 'trust.check.activationLock.notVerified',
      status: listing.imei_verified ? 'pass' : 'unknown',
    },
    {
      id: 'water',
      labelKey: 'trust.check.water.label',
      valueKey: verified ? 'trust.check.water.notDetected' : 'trust.check.notInspected',
      status: verified ? 'pass' : 'na',
    },
    {
      id: 'usage',
      labelKey: 'trust.check.usage.label',
      valueKey: usageKey ? `listing.usage.${usageKey}` : 'trust.check.notProvided',
      status: isLightUsage(listing.usage_duration) ? 'pass' : usageKey ? 'warn' : 'unknown',
    },
    {
      id: 'cosmetic',
      labelKey: 'trust.check.cosmetic.label',
      valueKey: 'trust.check.cosmetic.value',
      valueVars: { score: cosmeticScore },
      status: cosmeticScore >= 7 ? 'pass' : 'warn',
    },
    {
      id: 'parts',
      labelKey: 'trust.check.parts.label',
      valueKey: declaredParts ? 'trust.check.parts.originalDeclared' : 'trust.check.notProvided',
      valueVars: declaredParts ? { count: originalCount } : undefined,
      status: !declaredParts ? 'unknown' : aftermarket ? 'warn' : 'pass',
    },
  ]

  return items
}

/** Buyer-facing checklist — omits rows covered by dedicated listing-detail panels. */
export function trustChecklistForDisplay(listing: ListingTrustInput): TrustCheckItem[] {
  const manufacturer = listing.phone_model?.manufacturer
  const modelName = listing.phone_model?.model_name
  const parts = normalizePartsAuthenticity(listing.parts_authenticity)
  const faceIdDeclared = parts.face_id != null && parts.face_id.status !== 'unknown'

  return trustChecklist(listing).filter((item) => {
    if (item.id === 'battery' && hasBatteryTransparency(listing)) return false
    if (item.id === 'parts') return false
    // Shown in trust report summary rows (technical / cosmetic / grade / declaration).
    if (item.id === 'cosmetic') return false
    if (item.id === 'face_id' && !showFaceIdPart(manufacturer, modelName) && !faceIdDeclared) return false
    return true
  })
}

/** Simple best-value score for sorting cards (higher = better deal). */
export function bestValueScore(listing: ListingTrustInput): number {
  const trust = computeTrustScore(listing)
  const insight = priceInsight(listing)
  const priceInsightBonus = insight.level === 'great' ? 15 : insight.level === 'fair' ? 5 : 0
  const bat = declaredBatteryPct(listing)
  return trust + priceInsightBonus + (bat != null ? bat * 0.1 : 0)
}
